import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Auth } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ShieldCheck, Phone, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const COLLAGE = [
  "https://images.unsplash.com/photo-1764512680324-048f158cab2b?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  "https://images.unsplash.com/photo-1767974785671-cfc4c8727147?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  "https://images.unsplash.com/photo-1771947674128-9a708933f171?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  "https://images.unsplash.com/photo-1770226415002-dbbd40327ec7?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
];

export default function LoginModal() {
  const { loginOpen, setLoginOpen, setUser } = useAuth();
  const [step, setStep] = useState(1); // 1 phone, 2 otp, 3 register
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [accept, setAccept] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reg, setReg] = useState({ first_name: "", last_name: "", email: "" });

  const reset = () => { setStep(1); setPhone(""); setOtp(""); setReg({ first_name: "", last_name: "", email: "" }); };
  const close = () => { setLoginOpen(false); setTimeout(reset, 300); };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    if (!accept) { toast.error("Please accept the Terms & Conditions"); return; }
    setLoading(true);
    try {
      const r = await Auth.sendOtp(`+91${phone}`, accept);
      toast.success(`OTP sent (demo). Use ${r.dev_otp || "123456"}.`);
      setStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send OTP"); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setLoading(true);
    try {
      const r = await Auth.verifyOtp(`+91${phone}`, otp);
      if (r.is_new) { setStep(3); } else { setUser(r.user); toast.success(`Welcome back, ${r.user.first_name || ""}!`); close(); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Invalid OTP"); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!reg.first_name || !reg.last_name || !reg.email) { toast.error("All fields are required"); return; }
    setLoading(true);
    try {
      const r = await Auth.register({ phone: `+91${phone}`, ...reg });
      setUser(r.user);
      toast.success("Welcome to Zelo!");
      close();
    } catch (err) { toast.error(err?.response?.data?.detail || "Registration failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={loginOpen} onOpenChange={(v) => (v ? setLoginOpen(true) : close())}>
      <DialogContent className="p-0 overflow-hidden max-w-4xl border-0 rounded-2xl" data-testid="login-modal">
        <DialogTitle className="sr-only">Login to Zelo</DialogTitle>
        <DialogDescription className="sr-only">Login or create your account using your mobile number.</DialogDescription>
        <div className="grid grid-cols-1 md:grid-cols-2 min-h-[560px]">
          {/* Left collage */}
          <div className="hidden md:grid grid-cols-2 grid-rows-2 gap-1 bg-neutral-100 relative">
            {COLLAGE.map((src, i) => (
              <img key={i} src={src} alt="" className="w-full h-full object-cover" />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-xs tracking-[0.2em] uppercase opacity-80 mb-2">Zelo Members Only</div>
              <div className="font-heading text-2xl leading-tight">The brands you know.<br/>Now paying you rent.</div>
            </div>
          </div>

          {/* Right form */}
          <div className="p-8 md:p-10 flex flex-col">
            <div className="mb-8">
              <div className="font-heading text-3xl font-medium tracking-tight">
                {step === 1 && "Get Started"}
                {step === 2 && "Verify OTP"}
                {step === 3 && "Tell us about you"}
              </div>
              <div className="text-sm text-neutral-500 mt-2">
                {step === 1 && "Enter your phone number. You'll receive a 6-digit code."}
                {step === 2 && `We sent a code to +91 ${phone}`}
                {step === 3 && "Quick details so we can personalise your experience."}
              </div>
            </div>

            {step === 1 && (
              <form onSubmit={handleSendOtp} className="space-y-5" data-testid="login-step-phone">
                <div className="rounded-xl border border-neutral-200 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20 transition flex items-center px-4 py-3">
                  <span className="text-sm font-medium text-neutral-500 mr-3 flex items-center gap-1.5"><Phone className="w-4 h-4" /> +91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="Enter Mobile Number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="flex-1 outline-none text-base"
                    data-testid="login-phone-input"
                  />
                </div>
                <label className="flex items-start gap-2 text-sm text-neutral-600">
                  <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1 accent-[#5D33F6]" data-testid="login-tnc-checkbox" />
                  <span>I agree to Zelo's <a className="text-brand underline" href="/terms">Terms & Conditions</a></span>
                </label>
                <button disabled={loading} type="submit" className="w-full rounded-full bg-brand text-white font-semibold py-3 hover:bg-brand-600 transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="login-send-otp-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[11px] text-neutral-400 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Demo mode — any number works. Use OTP <strong>123456</strong>.</p>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleVerify} className="space-y-5" data-testid="login-step-otp">
                <div className="rounded-xl border border-neutral-200 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20 transition px-4 py-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="w-full outline-none text-base tracking-[0.5em] font-mono"
                    data-testid="login-otp-input"
                  />
                </div>
                <button disabled={loading} type="submit" className="w-full rounded-full bg-brand text-white font-semibold py-3 hover:bg-brand-600 transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="login-verify-otp-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Verify <ArrowRight className="w-4 h-4" /></>}
                </button>
                <button type="button" onClick={() => setStep(1)} className="text-sm text-neutral-500 hover:text-brand">← Change number</button>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={handleRegister} className="space-y-4" data-testid="login-step-register">
                <div className="grid grid-cols-2 gap-3">
                  <input className="rounded-xl border border-neutral-200 px-4 py-3 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" placeholder="First Name" value={reg.first_name} onChange={(e) => setReg({ ...reg, first_name: e.target.value })} data-testid="register-first-name" />
                  <input className="rounded-xl border border-neutral-200 px-4 py-3 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" placeholder="Last Name" value={reg.last_name} onChange={(e) => setReg({ ...reg, last_name: e.target.value })} data-testid="register-last-name" />
                </div>
                <input type="email" className="w-full rounded-xl border border-neutral-200 px-4 py-3 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" placeholder="Email Address" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} data-testid="register-email" />
                <button disabled={loading} type="submit" className="w-full rounded-full bg-brand text-white font-semibold py-3 hover:bg-brand-600 transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="register-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Finish & Browse <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
